import React from 'react'
import { makeStyles } from '@material-ui/styles'
import { Box, Typography } from '@material-ui/core'
import { mutualTheme } from '~/utils/theme'
import { CheckIcon, ClockIcon, CloseIcon, ThreeDotsIcon } from '~/assets/icons'
import { useRouteMatch } from 'react-router-dom'

export type DocumentationOnboardStatusProps = {
  label: string,
  path?: string,
  success?: boolean,
  error?: boolean,
  warning?: boolean,
  waiting?: boolean,
  validating?: boolean,
  isExact?: boolean
}

const isValidStatus = (props: DocumentationOnboardStatusProps): boolean => {
  const { success, error, warning, waiting, validating } = props

  if (success || error || warning || waiting || validating) {
    return true
  }

  return false
}

const getStatusColor = (props: DocumentationOnboardStatusProps): string => {
  const { success, error, warning, waiting, validating } = props

  if (error) {
    return mutualTheme.palette.error.main
  }

  if (warning) {
    return '#FBCFBD'
  }

  if (waiting) {
    return '#F2B714'
  }

  if (validating) {
    return '#5DA0FD'
  }

  if (success) {
    return '#BEE3CF'
  }

  return mutualTheme.palette.grey[400]
}

const getStatusIcon = (props: DocumentationOnboardStatusProps): JSX.Element | null => {
  const { success, error, warning, waiting, validating } = props

  if (error) {
    return <CloseIcon htmlColor={mutualTheme.palette.common.white} />
  }

  if (warning) {
    return <ThreeDotsIcon />
  }

  if (waiting || validating) {
    return <ClockIcon htmlColor='white' />
  }

  if (success) {
    return <CheckIcon htmlColor='#2EA561' />
  }

  return null
}

const useStyles = makeStyles({
  iconContainer: {
    width: 35,
    height: 35,
    display: 'flex',
    alignItems: 'center',
    borderRadius: '100%',
    justifyContent: 'center',
    marginRight: mutualTheme.spacing(1),
    color: (props: DocumentationOnboardStatusProps) => getStatusColor(props),
    backgroundColor: (props: DocumentationOnboardStatusProps) => getStatusColor(props)
  },
  label: {
    fontWeight: (props: DocumentationOnboardStatusProps) => isValidStatus(props) ? 500 : 400
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    marginTop: mutualTheme.spacing(2),
    marginBottom: mutualTheme.spacing(2)
  }
})

export const OnboardingStatus: React.FC<DocumentationOnboardStatusProps> = (props) => {
  const { label, path, isExact } = props
  const match = useRouteMatch({ path, exact: isExact })
  const classes = useStyles(props)

  return (
    <Box className={classes.container}>
      <Box
        className={classes.iconContainer}
      >
        {getStatusIcon(props)}
      </Box>
      <Typography
        variant='airbnbBody1'
        className={classes.label}
        sx={{ color: match ? 'textBlack.main' : 'textGray2.main' }}
      >
        {label}
      </Typography>
    </Box>
  )
}
